import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services';
import { utils } from '../../utils';
import FilterPanel from '../../components/Common/FilterPanel';
import ResultsCounter from '../../components/Common/ResultsCounter';
import LoadMoreButton from '../../components/Common/LoadMoreButton';
import { usePermissions } from '../../hooks/usePermissions';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { SystemModule } from '../../types/permissions';

interface Schedule {
  id: string;
  numero_agendamento: string;
  titulo: string;
  descricao: string;
  tipo_agendamento_id: string;
  tipo_agendamento_nome?: string;
  status: string;
  data_inicio: string;
  data_fim: string;
  veiculo_id?: string;
  motorista_id?: string;
  local_agendamento?: string;
  prioridade: string;
  observacoes?: string;
  lembrete_enviado?: boolean;
  ativo: boolean;
  concluido: boolean;
  cancelado: boolean;
  alta_prioridade: boolean;
  criado_em: string;
  atualizado_em: string;
  veiculo?: { id?: number | string; placa?: string; marca?: string; modelo?: string };
  motorista?: { id?: number | string; nome?: string };
}

// Função para obter o nome do tipo de agendamento a partir da resposta da API
const getTipoAgendamentoNome = (schedule: Schedule): string => {
  return schedule.tipo_agendamento_nome || 'Outro';
};

const Schedules: React.FC = () => {
  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Hook de infinite scroll
  const {
    items: schedules,
    isLoading,
    totalItems,
    displayedItems,
    hasMore,
    loadMore
  } = useInfiniteScroll<Schedule>({
    fetchFunction: async (page, limit, filters) => {
      try {
        const response = await apiService.getSchedules({
          page,
          limit,
          status: filters?.status || undefined,
          tipo: filters?.tipo || undefined,
          prioridade: filters?.prioridade || undefined
        });

        if (response.success && response.data && response.data.agendamentos) {
          const agendamentos = response.data.agendamentos;

          return {
            success: true,
            data: {
              items: agendamentos,
              pagination: {
                total: (response.data as any).pagination?.total || agendamentos.length
              }
            }
          };
        }

        return {
          success: true,
          data: { items: [], pagination: { total: 0 } }
        };
      } catch (error) {
        console.error('Erro ao carregar agendamentos:', error);
        return {
          success: false,
          data: [],
          message: 'Erro ao carregar agendamentos'
        };
      }
    },
    itemsPerPage: 10,
    filters: { status: statusFilter, tipo: typeFilter, prioridade: priorityFilter },
    onError: (error) => {
      utils.showNotification(error, { type: 'error' });
    }
  });

  // O backend retorna status/prioridade em minúsculo (agendado, concluido,
  // alta, ...). Normalizamos para evitar divergência de case nas comparações.
  const normStatus = (s?: string): string => (s || '').toLowerCase();
  const normPriority = (p?: string): string => (p || '').toLowerCase();

  const getStatusClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      'agendado': 'status-info',
      'confirmado': 'status-info',
      'concluido': 'status-success',
      'em_andamento': 'status-warning',
      'reagendado': 'status-secondary',
      'cancelado': 'status-danger'
    };
    return statusMap[normStatus(status)] || 'status-pending';
  };

  const getStatusText = (status: string): string => {
    const map: Record<string, string> = {
      'agendado': 'Agendado',
      'confirmado': 'Confirmado',
      'em_andamento': 'Em Andamento',
      'concluido': 'Concluído',
      'cancelado': 'Cancelado',
      'reagendado': 'Reagendado'
    };
    return map[normStatus(status)] || (status || '-');
  };

  const getPriorityClass = (prioridade: string): string => {
    const priorityMap: Record<string, string> = {
      'urgente': 'status-danger',
      'alta': 'status-danger',
      'normal': 'status-warning',
      'baixa': 'status-info'
    };
    return priorityMap[normPriority(prioridade)] || 'status-secondary';
  };

  const getPriorityText = (prioridade: string): string => {
    const map: Record<string, string> = {
      'baixa': 'Baixa',
      'normal': 'Normal',
      'alta': 'Alta',
      'urgente': 'Urgente'
    };
    return map[normPriority(prioridade)] || (prioridade || '-');
  };


  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatTime = (dateString: string): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysUntil = (dateString: string): number => {
    if (!dateString) return 0;
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const filteredSchedules = schedules.filter(schedule => {
    const matchesSearch = (schedule.titulo || '').toLowerCase().includes(filter.toLowerCase()) ||
                         (schedule.descricao || '').toLowerCase().includes(filter.toLowerCase()) ||
                         (schedule.local_agendamento && schedule.local_agendamento.toLowerCase().includes(filter.toLowerCase()));
    const matchesType = !typeFilter || getTipoAgendamentoNome(schedule) === typeFilter;
    const matchesStatus = !statusFilter || schedule.status === statusFilter;
    const matchesPriority = !priorityFilter || schedule.prioridade === priorityFilter;
    return matchesSearch && matchesType && matchesStatus && matchesPriority;
  });

  const viewSchedule = (id: string) => {
    navigate(`/agendamentos/visualizar/${id}`);
  };

  const editSchedule = (id: string) => {
    navigate(`/agendamentos/editar/${id}`);
  };

  const deleteSchedule = async (id: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm('Tem certeza que deseja excluir este agendamento?')) {
      try {
        const response = await apiService.deleteSchedule(id);
        if (response.success) {
          utils.showNotification('Agendamento excluído com sucesso!', { type: 'success' });
          // Recarrega a lista após deletar
          window.location.reload();
        } else {
          utils.showNotification('Erro ao excluir agendamento', { type: 'error' });
        }
      } catch (error) {
        console.error('Erro ao excluir agendamento:', error);
        utils.showNotification('Erro ao excluir agendamento', { type: 'error' });
      }
    }
  };

  const getTypes = () => {
    const types = Array.from(new Set(schedules.map(s => getTipoAgendamentoNome(s))));
    return types;
  };

  const getStatuses = () => {
    const statuses = Array.from(new Set(schedules.map(s => s.status)));
    return statuses;
  };

  const getPriorities = () => {
    const priorities = Array.from(new Set(schedules.map(s => s.prioridade)));
    return priorities;
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Agendamentos</h1>
          <button className="btn btn-primary" disabled>
            <i className="fas fa-plus"></i>
            Novo Agendamento
          </button>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando agendamentos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Agendamentos</h1>
        {canCreate(SystemModule.AGENDAMENTOS) && (
          <button
            className="btn btn-primary"
            onClick={() => navigate('/agendamentos/novo')}
          >
            <i className="fas fa-plus"></i>
            Novo Agendamento
          </button>
        )}
      </div>

      <div className="page-content">
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Calendário de Agendamentos</h2>
            <FilterPanel
              fields={[
                {
                  type: 'text',
                  label: 'Buscar',
                  value: filter,
                  onChange: setFilter,
                  placeholder: 'Título, descrição, veículo ou local...'
                },
                {
                  type: 'select',
                  label: 'Tipo',
                  value: typeFilter,
                  onChange: setTypeFilter,
                  options: [
                    { value: '', label: 'Todos' },
                    ...getTypes().map(type => ({ value: type, label: type }))
                  ]
                },
                {
                  type: 'select',
                  label: 'Status',
                  value: statusFilter,
                  onChange: setStatusFilter,
                  options: [
                    { value: '', label: 'Todos' },
                    ...getStatuses().map(status => ({ value: status, label: getStatusText(status) }))
                  ]
                },
                {
                  type: 'select',
                  label: 'Prioridade',
                  value: priorityFilter,
                  onChange: setPriorityFilter,
                  options: [
                    { value: '', label: 'Todas' },
                    ...getPriorities().map(priority => ({ value: priority, label: getPriorityText(priority) }))
                  ]
                }
              ]}
              onFilter={() => {}}
              onClear={() => {
                setFilter('');
                setTypeFilter('');
                setStatusFilter('');
                setPriorityFilter('');
              }}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              title="Filtros de Agendamentos"
            />
          </div>
          
          <div className="card-body">
            {/* Contador de resultados */}
            {filteredSchedules.length > 0 && (
              <ResultsCounter
                displayedItems={displayedItems}
                totalItems={totalItems}
                isLoading={isLoading}
                label="agendamentos"
              />
            )}

            {filteredSchedules.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-calendar-alt empty-state-icon"></i>
                <h3>Nenhum agendamento encontrado</h3>
                <p>{filter || typeFilter || statusFilter || priorityFilter ? 'Tente ajustar os filtros de busca.' : 'Crie o primeiro agendamento do sistema.'}</p>
                {canCreate(SystemModule.AGENDAMENTOS) && (
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate('/agendamentos/novo')}
                  >
                    Criar Primeiro Agendamento
                  </button>
                )}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Título</th>
                      <th>Tipo</th>
                      <th>Data/Hora</th>
                      <th>Veículo</th>
                      <th>Local</th>
                      <th>Prioridade</th>
                      <th>Status</th>
                      <th>Repetição</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSchedules.map(schedule => {
                      const daysUntil = getDaysUntil(schedule.data_inicio);
                      return (
                        <tr key={schedule.id}>
                          <td>
                            <strong>{schedule.titulo}</strong>
                            {schedule.descricao && (
                              <div style={{ fontSize: '0.8rem', color: '#7f8c8d', maxWidth: '200px' }}>
                                {schedule.descricao}
                              </div>
                            )}
                          </td>
                          <td>
                            <span className="status-badge status-info">
                              {getTipoAgendamentoNome(schedule)}
                            </span>
                          </td>
                          <td>
                            <div style={{ fontSize: '0.8rem' }}>
                              {formatDate(schedule.data_inicio)}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#7f8c8d' }}>
                              {formatTime(schedule.data_inicio)} - {formatTime(schedule.data_fim)}
                            </div>
                            {daysUntil > 0 && normStatus(schedule.status) === 'agendado' && (
                              <div style={{ fontSize: '0.7rem', color: daysUntil <= 2 ? '#e74c3c' : '#f39c12' }}>
                                {daysUntil === 1 ? 'Amanhã' : `Em ${daysUntil} dias`}
                              </div>
                            )}
                          </td>
                          <td>{schedule.veiculo ? schedule.veiculo.placa : (schedule.veiculo_id ? `Veículo ${schedule.veiculo_id}` : '-')}</td>
                          <td>{schedule.local_agendamento || '-'}</td>
                          <td>
                            <span className={`status-badge ${getPriorityClass(schedule.prioridade)}`}>
                              {getPriorityText(schedule.prioridade)}
                            </span>
                          </td>
                          <td>
                            <span className={`status-badge ${getStatusClass(schedule.status)}`}>
                              {getStatusText(schedule.status)}
                            </span>
                          </td>
                          <td>
                            <span style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                              Única
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn-view"
                                onClick={() => viewSchedule(schedule.id)}
                                title="Visualizar"
                              >
                                <i className="fas fa-eye"></i>
                              </button>
                              {canUpdate(SystemModule.AGENDAMENTOS) && (
                                <button
                                  className="btn-edit"
                                  onClick={() => editSchedule(schedule.id)}
                                  title="Editar"
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                              )}
                              {canDelete(SystemModule.AGENDAMENTOS) && (
                                <button
                                  className="btn-delete"
                                  onClick={() => deleteSchedule(schedule.id)}
                                  title="Excluir"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <LoadMoreButton
                  onClick={loadMore}
                  isLoading={isLoading}
                  hasMore={hasMore}
                />
              </div>
            )}
          </div>
        </div>

        {/* Estatísticas dos agendamentos */}
        <div className="dashboard-grid" style={{ marginTop: '20px' }}>
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-calendar-alt"></i>
            </div>
            <div className="stat-value">
              {schedules.length}
            </div>
            <div className="stat-label">Total de Agendamentos</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <div className="stat-value">
              {schedules.filter(s => normStatus(s.status) === 'concluido').length}
            </div>
            <div className="stat-label">Concluídos</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-clock"></i>
            </div>
            <div className="stat-value">
              {schedules.filter(s => normStatus(s.status) === 'agendado').length}
            </div>
            <div className="stat-label">Agendados</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <div className="stat-value">
              {schedules.filter(s => normPriority(s.prioridade) === 'alta' && normStatus(s.status) === 'agendado').length}
            </div>
            <div className="stat-label">Alta Prioridade</div>
          </div>
        </div>

        {/* Próximos agendamentos */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Próximos Agendamentos</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              <div>
                <h4 style={{ color: '#e74c3c', marginBottom: '10px' }}>
                  <i className="fas fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
                  Agendamentos de Alta Prioridade
                </h4>
                <ul style={{ color: '#7f8c8d', fontSize: '0.9rem' }}>
                  {schedules
                    .filter(s => normPriority(s.prioridade) === 'alta' && normStatus(s.status) === 'agendado')
                    .sort((a, b) => new Date(a.data_inicio).getTime() - new Date(b.data_inicio).getTime())
                    .slice(0, 5)
                    .map(schedule => (
                      <li key={schedule.id} style={{ marginBottom: '8px' }}>
                        <strong>{schedule.titulo}</strong>
                        <div>{formatDate(schedule.data_inicio)} - {formatTime(schedule.data_inicio)}</div>
                        <div style={{ fontSize: '0.8rem' }}>{schedule.local_agendamento || 'Local não especificado'}</div>
                      </li>
                    ))
                  }
                  {schedules.filter(s => normPriority(s.prioridade) === 'alta' && normStatus(s.status) === 'agendado').length === 0 && (
                    <li>Nenhum agendamento de alta prioridade</li>
                  )}
                </ul>
              </div>

              <div>
                <h4 style={{ color: '#f39c12', marginBottom: '10px' }}>
                  <i className="fas fa-clock" style={{ marginRight: '8px' }}></i>
                  Próximos 7 Dias
                </h4>
                <ul style={{ color: '#7f8c8d', fontSize: '0.9rem' }}>
                  {schedules
                    .filter(s => normStatus(s.status) === 'agendado' && getDaysUntil(s.data_inicio) <= 7)
                    .sort((a, b) => new Date(a.data_inicio).getTime() - new Date(b.data_inicio).getTime())
                    .slice(0, 5)
                    .map(schedule => (
                      <li key={schedule.id} style={{ marginBottom: '8px' }}>
                        <strong>{schedule.titulo}</strong>
                        <div>{formatDate(schedule.data_inicio)} - {formatTime(schedule.data_inicio)}</div>
                        <div style={{ fontSize: '0.8rem' }}>
                          <span className={`status-badge ${getPriorityClass(schedule.prioridade)}`}>
                            {schedule.prioridade}
                          </span>
                        </div>
                      </li>
                    ))
                  }
                  {schedules.filter(s => normStatus(s.status) === 'agendado' && getDaysUntil(s.data_inicio) <= 7).length === 0 && (
                    <li>Nenhum agendamento para os próximos 7 dias</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Distribuição por tipo */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Distribuição por Tipo</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
              {getTypes().map(type => {
                const typeSchedules = schedules.filter(s => getTipoAgendamentoNome(s) === type);
                const percentage = Math.round((typeSchedules.length / schedules.length) * 100);
                
                return (
                  <div key={type} style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontWeight: '600', color: '#2c3e50' }}>{type}</span>
                      <span style={{ color: '#04C64F', fontWeight: 'bold' }}>{typeSchedules.length}</span>
                    </div>
                    <div style={{ background: '#e9ecef', borderRadius: '4px', height: '8px' }}>
                      <div 
                        style={{ 
                          background: 'linear-gradient(135deg, #04C64F, #03a542)', 
                          height: '100%', 
                          borderRadius: '4px', 
                          width: `${percentage}%` 
                        }}
                      ></div>
                    </div>
                    <div style={{ marginTop: '10px', fontSize: '0.8rem', color: '#7f8c8d' }}>
                      <div>Agendados: <strong>{typeSchedules.filter(s => normStatus(s.status) === 'agendado').length}</strong></div>
                      <div>Concluídos: <strong>{typeSchedules.filter(s => normStatus(s.status) === 'concluido').length}</strong></div>
                      <div>Em andamento: <strong>{typeSchedules.filter(s => normStatus(s.status) === 'em_andamento').length}</strong></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Schedules;
